/**
 * Streaming AI search overview
 *
 * Architecture
 * - `performStreamingSearch()` creates an `EventSource` request to `/api/search`.
 * - `Api/Search/ElasticSearchController.cs::SearchResultStream()` returns `text/event-stream`.
 * - The controller emits `results`, zero or more `summary`, optional `error`, and a final `completion` event.
 * - `requestId`/`activeRequestId` ensure stale EventSource callbacks cannot repaint the UI after a newer search starts.
 *
 * Diagram
 * [Search input]
 *      |
 *      v
 * [EikaSearch.performStreamingSearch]
 *      |
 *      v
 * EventSource GET /api/search?search=&scope=&page=
 *      |
 *      v
 * [ElasticSearchController.SearchResultStream]
 *      |-----------------------------> Search provider ---------------> event: results
 *      |-----------------------------> GenAI token stream (page 0) ---> event: summary*
 *      |--------------------------------------------------------------> event: completion
 *      v
 * [search.ts event handlers]
 *      |
 *      +--> render search hits
 *      +--> append streamed AI summary text
 *      +--> close stream and re-enable feedback buttons
 *
 * Functional description
 * 1. Page 0 clears previous state, updates the URL, and shows loading skeletons for the AI answer.
 * 2. The backend sends `results` as soon as search hits are available so the list can render immediately.
 * 3. On page 0, the backend then streams `summary` packets token-by-token from GenAI, which are appended live.
 * 4. `completion` is the authoritative end-of-stream signal used to close the EventSource and finish the request.
 * 5. `error` may be either a backend SSE packet or an EventSource transport failure.
 */
import { ExpandCard } from './expand';
import { pushToDataLayer } from './shared/common';
import { html, on } from './shared/dom';
import {
    generateLoadingSkeleton,
    createElement,
    isExperienceEditor,
    sgwLinkButtonIcon,
    piwikFormatName,
    ensureLLMNumberSpacing,
} from './shared/helpers';

interface SearchResultItem {
    Url: string;
    Title: string;
    Body_Content?: string;
    Meta_Description?: string;
    Elastictitle?: string;
}

interface SearchResults {
    TotalCount: number;
    Documents: SearchResultItem[];
    Aggregations: Record<string, string>;
}

interface ResultsPacketData {
    results: SearchResults;
    page: number;
    query: string;
    type: string;
}

interface SummaryPacketData {
    text: string;
}

interface StreamingEventEnvelope<T> {
    success?: string | boolean;
    message?: string;
    data?: T | null;
}

type TrueFalseEmpty = 'true' | 'false' | '';

const SEARCH_SCOPE = {
    ALL: 'all',
    CORPORATE: 'corporate',
    PRIVATE: 'private',
} as const;

type SearchScope = (typeof SEARCH_SCOPE)[keyof typeof SEARCH_SCOPE];

const QUERY_PARAM_KEY = {
    QUERY: 'search', // search term against the index
    SCOPE: 'scope', // search scope (all, corporate, private)
    PAGE: 'page', // pagination page number
} as const;

type QueryParamKey = (typeof QUERY_PARAM_KEY)[keyof typeof QUERY_PARAM_KEY];

class EikaSearch {
    private searchForm!: HTMLElement;

    private searchInput!: HTMLInputElement | null;

    private searchButton!: HTMLButtonElement | null;

    private searchResultsText!: HTMLElement | null;

    private searchResultsContainer!: HTMLElement | null;

    private searchResultsFilterContainer!: HTMLElement | null;

    private searchResults!: HTMLElement | null;

    private aiSearchContainer!: HTMLElement | null;

    private isCorporate: TrueFalseEmpty = 'false';

    private aiEnabled: boolean = false;

    private isExperienceEditor: boolean = false;

    private currentPage: number = 0;

    private currentQuery: string = '';

    private numFirstResults: number = 15;

    private aiSummaryBuffer: string = '';

    private lastToken: string = '';

    private nextRequestId: number = 0;

    private activeRequestId: number = 0;

    private eventSource: EventSource | null = null;

    private isPagingRequestInFlight: boolean = false;

    private readonly SELECTOR_LIKE_BUTTON = '.eika-elastic-search__ai-btn-like';

    private readonly SELECTOR_DISLIKE_BUTTON = '.eika-elastic-search__ai-btn-dislike';

    private readonly SELECTOR_AI_RESULT = '.eika-elastic-search__ai-result';

    private readonly SELECTOR_AI_SUMMARY = '.eika-elastic-search__ai-summary';

    private readonly SELECTOR_AI_CONTAINER = '.eika-elastic-search__aicontainer';

    private readonly SELECTOR_AI_ACTION_BUTTONS = '.eika-elastic-search__ai-actionbuttons';

    private readonly SELECTOR_AI_INFO = '.eika-elastic-search__ai-info';

    private readonly SELECTOR_AI_TOGGLE = '.eika-elastic-search__ai-toggle';

    private readonly SELECTOR_AI_TOGGLE_BUTTON = '.eika-elastic-search__ai-toggle-btn';

    private readonly SELECTOR_AI_FEEDBACK_MESSAGE = '.eika-elastic-search__ai-responsemessage';

    private readonly SELECTOR_SKELETON = '.search-results-skeleton';

    private readonly SELECTOR_PAGINATION_ERROR = '.eika-elastic-search__pagination-error';

    private readonly SELECTOR_SHOW_MORE = '.eika-elastic-search__show-more';

    private readonly SELECTOR_SHOW_MORE_BUTTON = '.eika-elastic-search__show-more button';

    private readonly SELECTOR_RESULTS_SHOWING = '.eika-elastic-search__form-results-showing';

    private readonly SELECTOR_RESULTS_TOTAL = '.eika-elastic-search__form-results-total';

    private readonly SELECTOR_HIDE_BEDRIFT = '.eika-elastic-search__hideBedrift';

    private readonly SELECTOR_CORPORATE_RADIO = '.eika-elastic-search__options-corporate input';

    private readonly SELECTOR_CORPORATE_OPTIONS_LABEL = '.eika-elastic-search__options-corporate label > span';

    private readonly SELECTOR_PRIVATE_RADIO = '.eika-elastic-search__options-private input';

    private readonly SELECTOR_PRIVATE_OPTIONS_LABEL = '.eika-elastic-search__options-private label > span';

    private readonly AI_SUMMARY_COLLAPSED_LINES = 7;

    private readonly AI_SUMMARY_FALLBACK_LINE_HEIGHT_PX = 24;

    private aiSummaryExpanded: boolean = false;

    private readonly AI_RESULT_CLASS_HAS_OVERFLOW = 'has-overflow';

    private readonly AI_RESULT_CLASS_COLLAPSED = 'is-collapsed';

    private readonly AI_RESULT_CLASS_EXPANDED = 'is-expanded';

    constructor(elm: HTMLElement) {
        if (elm) {
            this.searchForm = elm;
            this.searchInput = elm.querySelector('.eika-elastic-search__input');
            this.searchButton = elm.querySelector('.eika-elastic-search__button');
            this.searchResultsText = elm.querySelector('.eika-elastic-search__form-results');
            this.searchResultsContainer = elm.querySelector('.eika-elastic-search__results');
            this.searchResults = elm.querySelector('.eika-elastic-search__results-container');
            this.searchResultsFilterContainer = elm.querySelector('.eika-elastic-search__results-filter');
            this.aiSearchContainer = elm.querySelector('.eika-elastic-search__expandcard');
            this.setActionButtonsDisabled(true);

            const elasticCorporate = document.querySelector("meta[name='bedrift']") as HTMLMetaElement;

            if (elasticCorporate) {
                this.isCorporate = elasticCorporate.content as TrueFalseEmpty;
            }

            const checkAi = this.searchForm.getAttribute('data-ai');

            if ((checkAi || '').toLowerCase() === 'true') {
                this.aiEnabled = true;
            }

            this.isExperienceEditor = isExperienceEditor(window.location.href);
        }
    }

    init() {
        if (!this.searchForm || !this.searchInput) return;
        if (this.isExperienceEditor) return;

        const params = new URLSearchParams(window.location.search);
        const initialQuery = (params.get(QUERY_PARAM_KEY.QUERY) ?? '').toLocaleLowerCase();
        const initialScope = this.getInitialScope(params.get(QUERY_PARAM_KEY.SCOPE) ?? SEARCH_SCOPE.ALL);

        this.applyScopeSelection(initialScope);

        if (initialQuery) {
            this.searchInput.value = initialQuery;
            this.searchInput.focus();
            this.currentPage = 0;
            this.performStreamingSearch(initialQuery, initialScope, this.currentPage);
        } else {
            this.searchInput.focus();
        }

        if (this.searchResultsFilterContainer && this.isCorporateHidden()) {
            this.searchResultsFilterContainer.style.display = 'none';
        }

        const radioOptions = this.searchForm.querySelectorAll(
            '.eika-elastic-search__options .sgw-radio-option__input',
        ) as NodeListOf<HTMLInputElement>;

        on(radioOptions, 'change', () => {
            this.currentPage = 0;
            this.performStreamingSearch(this.searchInput?.value?.trim() ?? '', this.getSelectedScope());
        });

        ExpandCard.init();

        if (this.searchButton) {
            on(this.searchButton, 'click', () => {
                this.currentPage = 0;
                this.performStreamingSearch(
                    this.searchInput?.value?.trim() ?? '',
                    this.getSelectedScope(),
                    this.currentPage,
                );
            });
        }

        if (this.searchInput) {
            on(this.searchInput, 'keydown', (event) => {
                if (event.key === 'Enter') {
                    event.preventDefault();
                    this.currentPage = 0;
                    this.performStreamingSearch(
                        (this.searchInput?.value?.trim() ?? '').toLocaleLowerCase(),
                        this.getSelectedScope(),
                    );
                }
            });

            on(this.searchInput, 'input', () => {
                if (this.searchInput && this.searchInput.value === '') {
                    const currentUrl = new URL(window.location.href);
                    currentUrl.search = '';
                    window.history.pushState({}, document.title, currentUrl.toString());
                    this.setActionButtonsDisabled(true);
                    this.clearResults();
                }
            });
        }

        if (this.aiSearchContainer) {
            const likeBtn = this.aiSearchContainer.querySelector(this.SELECTOR_LIKE_BUTTON);
            const dislikeBtn = this.aiSearchContainer.querySelector(this.SELECTOR_DISLIKE_BUTTON);
            if (likeBtn && dislikeBtn) {
                on(likeBtn as HTMLElement, 'click', () => this.sendFeedback('like'));
                on(dislikeBtn as HTMLElement, 'click', () => this.sendFeedback('dislike'));
            }

            const aiToggleBtn = this.aiSearchContainer.querySelector<HTMLButtonElement>(this.SELECTOR_AI_TOGGLE_BUTTON);
            if (aiToggleBtn) {
                on(aiToggleBtn, 'click', () => {
                    this.aiSummaryExpanded = !this.aiSummaryExpanded;
                    if (!this.aiSummaryExpanded) {
                        this.searchInput?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'end',
                        });
                        this.searchInput?.setAttribute('tabindex', '-1');
                        this.searchInput?.focus({ preventScroll: true });
                    }
                    this.updateAiSummaryCollapseState();
                });
            }
        }
    }

    static GTM(query: string, type: string = '', resPriv: number = 0, resCorp: number = 0): void {
        pushToDataLayer({
            app_namespace: 'sitecore-search-page',
            event: 'search',
            action: 'sitecore-search',
            search_phrase: query.toLowerCase(),
            search_type_of: type,
            search_results_private: resPriv,
            search_results_corporate: resCorp,
        });
    }

    ToggleFeedbackMessage(show: boolean = false): void {
        const feedbackMessage = this.aiSearchContainer?.querySelector<HTMLElement>(this.SELECTOR_AI_FEEDBACK_MESSAGE);
        if (!feedbackMessage) {
            return;
        }

        if (show) {
            feedbackMessage.style.display = '';
        } else {
            feedbackMessage.style.display = 'none';
        }
    }

    setActionButtonsDisabled(disabled: boolean): void {
        const likeBtn = this.aiSearchContainer?.querySelector<HTMLButtonElement>(this.SELECTOR_LIKE_BUTTON);
        const dislikeBtn = this.aiSearchContainer?.querySelector<HTMLButtonElement>(this.SELECTOR_DISLIKE_BUTTON);
        if (likeBtn) likeBtn.disabled = disabled;
        if (dislikeBtn) dislikeBtn.disabled = disabled;
    }

    setAiActionButtonsVisible(visible: boolean): void {
        if (!this.aiSearchContainer) return;
        const actionButtons = this.aiSearchContainer.querySelector<HTMLElement>(this.SELECTOR_AI_ACTION_BUTTONS);

        if (visible) {
            if (!actionButtons) return;
            const actionButtonLike = actionButtons.querySelector<HTMLElement>(this.SELECTOR_LIKE_BUTTON);
            const actionButtonDislike = actionButtons.querySelector<HTMLElement>(this.SELECTOR_DISLIKE_BUTTON);
            actionButtons.style.removeProperty('display');
            if (actionButtonLike && actionButtonDislike) {
                actionButtonLike.setAttribute('data-track-linkname', this.currentQuery);
                actionButtonDislike.setAttribute('data-track-linkname', this.currentQuery);
            }
        } else {
            if (!actionButtons) return;
            actionButtons.style.display = 'none';
        }
    }

    renderAiSummarySkeleton(): void {
        const aiContainer = this.searchForm.querySelector(this.SELECTOR_AI_CONTAINER) as HTMLElement | null;
        const aiResult = aiContainer?.querySelector(this.SELECTOR_AI_RESULT) as HTMLElement | null;
        if (!aiResult) return;

        this.resetAiSummaryExpansionState();
        aiResult.innerHTML = '';
        this.searchLoadingSkeleton(3, aiResult);
    }

    removeAiSummarySkeleton(): void {
        const aiContainer = this.searchForm.querySelector(this.SELECTOR_AI_CONTAINER) as HTMLElement | null;
        const aiResult = aiContainer?.querySelector(this.SELECTOR_AI_RESULT) as HTMLElement | null;
        this.removeSearchLoadingSkeleton(aiResult);
    }

    hideAiSummaryContainerAndSkeleton(): void {
        const aiContainer = this.searchForm.querySelector(this.SELECTOR_AI_CONTAINER) as HTMLElement | null;
        const aiResult = aiContainer?.querySelector(this.SELECTOR_AI_RESULT) as HTMLElement | null;

        this.removeAiSummarySkeleton();
        if (aiResult) {
            aiResult.innerHTML = '';
        }
        if (aiContainer) {
            aiContainer.style.display = 'none';
        }
        this.setAiActionButtonsVisible(false);
        this.ToggleFeedbackMessage(false);
        this.resetAiSummaryExpansionState();
    }

    searchLoadingSkeleton(items: number = 5, target: HTMLElement | null = null): void {
        const wrapper = createElement('div', { class: 'search-results-skeleton' });
        for (let i = 0; i < items - 1; i += 1) {
            const skeleton = generateLoadingSkeleton(3);

            const firstSpan = skeleton.querySelector('span');
            if (firstSpan) {
                const innerSpan = firstSpan.querySelector('span');
                if (innerSpan) {
                    const randomWidth = Math.floor(Math.random() * 31) + 20;
                    innerSpan.style.width = `${randomWidth}%`;
                    innerSpan.style.height = `1.3rem`;
                }
            }
            wrapper.appendChild(skeleton);
        }

        if (target) {
            target.appendChild(wrapper);
            return;
        }

        const parent = this.searchResults?.parentNode;
        if (parent && this.searchResults) {
            parent.insertBefore(wrapper, this.searchResults);
        }
    }

    removeSearchLoadingSkeleton(target: HTMLElement | null = null): void {
        const skeleton = (target ?? this.searchResultsContainer)?.querySelector(this.SELECTOR_SKELETON) as
            HTMLElement | undefined;
        if (skeleton) {
            skeleton.style.transition = 'opacity 0.2s ease';
            skeleton.style.opacity = '0';
            setTimeout(() => skeleton.remove(), 50);
        }
    }

    async sendFeedback(type: 'like' | 'dislike') {
        this.setActionButtonsDisabled(true);
        this.setAiActionButtonsVisible(false);
        this.ToggleFeedbackMessage(true);

        console.log(`User feedback: ${type}`);
    }

    /**
     * Starts one search stream from the browser side.
     *
     * For the first page this resets the visible state, shows the AI skeleton,
     * and then opens the SSE connection that `ElasticSearchController.SearchResultStream`
     * writes to. Pagination uses the same SSE endpoint, but skips AI summary rendering.
     */
    async performStreamingSearch(query: string, scope: SearchScope, page: number = 0) {
        if (!query) return null;

        const requestId = this.startRequest(page);
        if (page === 0) {
            this.setActionButtonsDisabled(true);
            this.clearResults();
            this.removeInlineErrorMessage();
            this.aiSummaryBuffer = '';
            this.lastToken = '';
        }

        query = query.toLocaleLowerCase();
        this.currentQuery = query;

        this.updateUrlState(query, scope, page);

        const requestScope = this.scopeToRequestScope(scope);
        const searchParams = new URLSearchParams();
        searchParams.set(QUERY_PARAM_KEY.QUERY, query);
        searchParams.set(QUERY_PARAM_KEY.SCOPE, requestScope);
        searchParams.set(QUERY_PARAM_KEY.PAGE, page.toString());
        const searchUrl = `/api/search?${searchParams.toString()}`;

        this.searchResultsContainer?.style.removeProperty('display');

        if (this.aiEnabled && page === 0) {
            const aiContainer = this.searchForm.querySelector(this.SELECTOR_AI_CONTAINER) as HTMLElement | null;
            if (aiContainer) {
                aiContainer.querySelector('.eika-elastic-search__ai')?.classList.remove('no-length-content');
                aiContainer.style.removeProperty('display');
            }

            this.setAiActionButtonsVisible(false);
            this.ToggleFeedbackMessage(false);
            this.renderAiSummarySkeleton();
        }

        try {
            await this.streamSearchFromBackend(searchUrl, requestId);
        } catch (error) {
            if (this.isStaleRequest(requestId)) {
                return null;
            }
            this.displayError(error, page);
            if (page === 0) {
                this.clearResults();
                this.renderGenericErrorState();
            } else {
                this.renderInlinePaginationError();
            }
            throw error;
        } finally {
            this.finishRequest(requestId, page);
        }
        return null;
    }

    /**
     * Marks a request as current so older EventSource callbacks can be ignored as stale.
     */
    startRequest(page: number): number {
        const requestId = this.nextRequestId + 1;
        this.nextRequestId = requestId;
        this.activeRequestId = requestId;
        if (page > 0) {
            this.isPagingRequestInFlight = true;
            this.setShowMoreDisabled(true);
        }
        return requestId;
    }

    /**
     * Finalizes request-local UI state once the active stream has ended.
     */
    finishRequest(requestId: number, page: number): void {
        if (this.isStaleRequest(requestId)) return;
        if (page > 0) {
            this.isPagingRequestInFlight = false;
            this.setShowMoreDisabled(false);
        }
    }

    /**
     * Guards the UI from race conditions when multiple searches are started in quick succession.
     */
    isStaleRequest(requestId: number): boolean {
        return requestId !== this.activeRequestId;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    updateUrlState(query: string, scope?: SearchScope, page?: number): void {
        const params = new URLSearchParams(window.location.search);
        this.setQueryParam(params, QUERY_PARAM_KEY.QUERY, query);

        /*
        if (scope === SEARCH_SCOPE.CORPORATE || scope === SEARCH_SCOPE.PRIVATE) {
            this.setQueryParam(params, QUERY_PARAM_KEY.SCOPE, scope);
        }

        if (page > 0) {
            this.setQueryParam(params, QUERY_PARAM_KEY.PAGE, page.toString());
        }
        */

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState(null, '', newUrl);
    }

    setQueryParam(params: URLSearchParams, key: QueryParamKey, value: string): void {
        params.set(key, value);
    }

    parseScope(value: string | null | undefined): SearchScope {
        if (!value) return SEARCH_SCOPE.ALL;
        const normalized = value.toLowerCase();
        if (
            normalized === SEARCH_SCOPE.CORPORATE ||
            normalized === SEARCH_SCOPE.PRIVATE ||
            normalized === SEARCH_SCOPE.ALL
        ) {
            return normalized;
        }
        return SEARCH_SCOPE.ALL;
    }

    getInitialScope(urlScope: string | null): SearchScope {
        const parsedScope = this.parseScope(urlScope);
        if (parsedScope !== SEARCH_SCOPE.ALL) {
            return parsedScope;
        }
        if (this.isCorporate === 'true') return SEARCH_SCOPE.CORPORATE;
        if (this.isCorporate === 'false') return SEARCH_SCOPE.PRIVATE;
        return SEARCH_SCOPE.ALL;
    }

    applyScopeSelection(scope: SearchScope): void {
        if (!this.searchResultsFilterContainer) return;
        const privRadio = this.searchResultsFilterContainer.querySelector<HTMLInputElement>(
            `input[name="filter"][value="${SEARCH_SCOPE.PRIVATE}"]`,
        );
        const corpRadio = this.searchResultsFilterContainer.querySelector<HTMLInputElement>(
            `input[name="filter"][value="${SEARCH_SCOPE.CORPORATE}"]`,
        );
        if (privRadio) privRadio.checked = scope === SEARCH_SCOPE.PRIVATE;
        if (corpRadio) corpRadio.checked = scope === SEARCH_SCOPE.CORPORATE;
    }

    getSelectedScope(): SearchScope {
        if (!this.searchResultsFilterContainer) return SEARCH_SCOPE.ALL;
        const selected =
            this.searchResultsFilterContainer.querySelector<HTMLInputElement>('input[name="filter"]:checked');
        if (!selected) return SEARCH_SCOPE.ALL;
        return selected.value === SEARCH_SCOPE.CORPORATE ? SEARCH_SCOPE.CORPORATE : SEARCH_SCOPE.PRIVATE;
    }

    scopeToRequestScope(scope: SearchScope): string {
        return scope;
    }

    setShowMoreDisabled(disabled: boolean): void {
        const showMoreBtn = this.searchForm.querySelector(this.SELECTOR_SHOW_MORE_BUTTON) as HTMLButtonElement | null;
        if (!showMoreBtn) return;
        showMoreBtn.disabled = disabled;
    }

    removeShowMoreButton(): void {
        const showMoreBtn = this.searchForm.querySelector(this.SELECTOR_SHOW_MORE);
        if (showMoreBtn) {
            showMoreBtn.remove();
        }
    }

    /**
     * Closes the currently active SSE connection before a new one is created or when a stream ends.
     */
    closeEventSource(): void {
        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }
    }

    /**
     * Subscribes to the SSE contract exposed by `ElasticSearchController.SearchResultStream`.
     *
     * Expected events:
     * - `results`: one packet with hits, pagination metadata, and scope information
     * - `summary`: zero or more GenAI tokens for page 0
     * - `completion`: final packet that closes the stream cleanly
     * - `error`: either a backend-generated packet or a transport-level EventSource failure
     */
    streamSearchFromBackend(url: string, requestId: number): Promise<void> {
        this.closeEventSource();
        this.eventSource = new EventSource(url);

        return new Promise((resolve, reject) => {
            const source = this.eventSource;
            if (!source) {
                reject(new Error('Unable to create EventSource'));
                return;
            }

            let settled = false;

            const cleanup = () => {
                if (this.eventSource === source) {
                    source.close();
                    this.eventSource = null;
                }
            };

            const finalizeResolve = () => {
                if (settled) return;
                settled = true;
                cleanup();
                resolve();
            };

            const finalizeReject = (error: Error) => {
                if (settled) return;
                settled = true;
                cleanup();

                if (
                    !this.aiSummaryBuffer &&
                    !this.isStaleRequest(requestId) &&
                    this.aiEnabled &&
                    this.currentPage === 0
                ) {
                    this.hideAiSummaryContainerAndSkeleton();
                }

                reject(error);
            };

            const clearEmptyAiResult = () => {
                // If no summary tokens were received, clear any remaining AI skeleton
                if (
                    this.aiEnabled &&
                    !this.aiSummaryBuffer &&
                    !this.isStaleRequest(requestId) &&
                    this.currentPage === 0
                ) {
                    const aiContainer = this.searchForm.querySelector(this.SELECTOR_AI_CONTAINER) as HTMLElement | null;
                    const aiResult = aiContainer?.querySelector(this.SELECTOR_AI_RESULT) as HTMLElement | null;
                    if (aiResult) {
                        aiResult.innerHTML = '';
                    }
                }
            };

            source.addEventListener('results', (event) => {
                if (settled) return;
                if (this.isStaleRequest(requestId)) {
                    finalizeResolve();
                    return;
                }

                let payload: ResultsPacketData | null = null;
                const messageEvent = event as MessageEvent;
                if (messageEvent.data) {
                    try {
                        const envelope = JSON.parse(messageEvent.data) as StreamingEventEnvelope<ResultsPacketData>;
                        payload = envelope.data ?? null;
                    } catch (e) {
                        finalizeReject(
                            e instanceof Error ? e : new Error('Unable to parse EventSource results payload'),
                        );
                        return;
                    }
                }

                try {
                    this.handleResultsEvent(payload);
                } catch (e) {
                    finalizeReject(e instanceof Error ? e : new Error('Failed to handle results packet'));
                } finally {
                    if (this.aiEnabled) {
                        this.removeSearchLoadingSkeleton();
                    }
                }
            });

            source.addEventListener('summary', (event) => {
                if (settled) return;
                if (this.isStaleRequest(requestId)) {
                    finalizeResolve();
                    return;
                }

                let payload: SummaryPacketData | null = null;
                const messageEvent = event as MessageEvent;
                if (messageEvent.data) {
                    try {
                        const envelope = JSON.parse(messageEvent.data) as StreamingEventEnvelope<SummaryPacketData>;
                        payload = envelope.data ?? null;
                    } catch (e) {
                        finalizeReject(
                            e instanceof Error ? e : new Error('Unable to parse EventSource summary payload'),
                        );
                        return;
                    }
                }

                try {
                    this.handleSummaryEvent(payload);
                } catch (e) {
                    finalizeReject(e instanceof Error ? e : new Error('Failed to handle summary packet'));
                }
            });

            source.addEventListener('completion', () => {
                if (settled) return;
                if (this.isStaleRequest(requestId)) {
                    finalizeResolve();
                    return;
                }

                try {
                    this.handleCompletionEvent();
                } catch (e) {
                    finalizeReject(e instanceof Error ? e : new Error('Failed to handle completion packet'));
                    return;
                }

                clearEmptyAiResult();
                finalizeResolve();
            });

            source.addEventListener('error', (event) => {
                if (settled) return;

                // Support both backend "error" packets (MessageEvent) and EventSource transport errors.
                if ('data' in event && (event as MessageEvent).data) {
                    if (this.isStaleRequest(requestId)) {
                        finalizeResolve();
                        return;
                    }

                    this.handleErrorEvent(event as MessageEvent);
                    if (!this.aiSummaryBuffer && this.aiEnabled && this.currentPage === 0) {
                        this.hideAiSummaryContainerAndSkeleton();
                    }
                    return;
                }

                if (this.isStaleRequest(requestId)) {
                    finalizeResolve();
                    return;
                }

                finalizeReject(new Error('EventSource connection failed'));
            });
        });
    }

    /**
     * Handles the controller's `results` packet and renders the search hits immediately.
     */
    handleResultsEvent(payload: ResultsPacketData | null): void {
        if (!payload || !payload.results) {
            if (payload && payload.page > 0) {
                this.removeShowMoreButton();
            } else {
                this.clearResults();
                this.noResults();
            }
            return;
        }

        const typ = this.scopeFromBedriftValue(payload.type);
        this.populateResults(payload.results, payload.page, payload.query, typ);
    }

    /**
     * Handles one streamed GenAI token from the controller's `summary` event.
     */
    handleSummaryEvent(payload: SummaryPacketData | null): void {
        if (this.aiEnabled && this.currentPage === 0 && payload) {
            this.appendStreamingSummaryText(payload.text);
        }
    }

    /**
     * Handles the backend's explicit end-of-stream signal and restores post-search UI actions.
     */
    handleCompletionEvent(): void {
        this.lastToken = '';
        if (this.aiEnabled && this.currentPage === 0) {
            const aiContainer = this.searchForm.querySelector(this.SELECTOR_AI_CONTAINER) as HTMLElement | null;
            const aiResult = aiContainer?.querySelector(this.SELECTOR_AI_RESULT) as HTMLElement | null;

            if (this.aiSummaryBuffer === '') {
                this.hideAiSummaryContainerAndSkeleton();
            } else {
                this.setActionButtonsDisabled(false);
                const trueHeight = aiResult?.scrollHeight ?? 0;
                if (aiContainer && trueHeight < 100) {
                    aiContainer.querySelector('.eika-elastic-search__ai')?.classList.add('no-length-content');
                }
            }
        }
    }

    /**
     * Handles backend `error` packets carried over SSE.
     * Transport failures are handled separately in the EventSource `error` listener.
     */
    handleErrorEvent(messageEvent: MessageEvent): void {
        let message = '';
        try {
            const envelope = JSON.parse(messageEvent.data) as StreamingEventEnvelope<unknown>;
            message = envelope.message ?? '';
        } catch {
            message = typeof messageEvent.data === 'string' ? messageEvent.data : '';
        }

        console.log(message);
        this.lastToken = '';
    }

    scopeFromBedriftValue(value: string): SearchScope {
        if (value === SEARCH_SCOPE.CORPORATE || value === SEARCH_SCOPE.PRIVATE) return value;
        return SEARCH_SCOPE.ALL;
    }

    /**
     * Appends one streamed summary fragment to the AI result area while preserving readable spacing.
     */
    appendStreamingSummaryText(text: string) {
        const aiContainer = this.searchForm.querySelector(this.SELECTOR_AI_CONTAINER) as HTMLElement | null;
        const aiResult = aiContainer?.querySelector(this.SELECTOR_AI_RESULT) as HTMLElement | null;

        if (!aiResult || !text) {
            this.lastToken = '';
            return;
        }

        // First token: remove skeleton and show feedback controls (still disabled until completion).
        if (!this.aiSummaryBuffer) {
            this.removeAiSummarySkeleton();
            this.setAiActionButtonsVisible(true);
            this.ToggleFeedbackMessage(false);
        }

        // LLM tokenizers can omit the leading space before numbers.
        // Insert one when a digit-starting token follows a word character.
        const newText = ensureLLMNumberSpacing(text, this.lastToken, this.aiSummaryBuffer);
        this.lastToken = text;

        this.aiSummaryBuffer += newText;
        aiResult.innerHTML = this.aiSummaryBuffer;
        this.updateAiSummaryCollapseState();
    }

    getAiSummaryLineHeightPx(aiResult: HTMLElement): number {
        const computedLineHeight = window.getComputedStyle(aiResult).lineHeight;
        if (!computedLineHeight || computedLineHeight === 'normal') {
            return this.AI_SUMMARY_FALLBACK_LINE_HEIGHT_PX;
        }
        const parsed = Number.parseFloat(computedLineHeight);
        if (Number.isNaN(parsed) || parsed <= 0) {
            return this.AI_SUMMARY_FALLBACK_LINE_HEIGHT_PX;
        }
        return parsed;
    }

    static setAiSummaryCollapsedDimensions(aiResult: HTMLElement, collapsedHeight: number): void {
        // Keep the AI box at a stable height while content streams in, so the page does not jump.
        aiResult.style.height = `${collapsedHeight}px`;
        aiResult.style.overflow = 'hidden';
    }

    static getAiSummaryToggleLabels(button: HTMLButtonElement): { more: string; less: string } {
        const more = button.dataset.labelMore ?? 'Vis mer';
        const less = button.dataset.labelLess ?? 'Vis mindre';
        return { more, less };
    }

    updateAiSummaryCollapseState(): void {
        const aiContainer = this.searchForm.querySelector(this.SELECTOR_AI_CONTAINER) as HTMLElement | null;
        const aiSummary = aiContainer?.querySelector<HTMLElement>(this.SELECTOR_AI_SUMMARY);
        const aiResult = aiContainer?.querySelector(this.SELECTOR_AI_RESULT) as HTMLElement | null;
        const aiInfo = aiContainer?.querySelector<HTMLElement>(this.SELECTOR_AI_INFO);
        const aiToggle = aiContainer?.querySelector<HTMLElement>(this.SELECTOR_AI_TOGGLE);
        const aiToggleButton = aiToggle?.querySelector<HTMLButtonElement>(this.SELECTOR_AI_TOGGLE_BUTTON);
        const aiToggleButtonLabel = aiToggleButton?.querySelector<HTMLSpanElement>('.sgw-button-label');
        if (!aiSummary || !aiResult || !aiToggle || !aiToggleButton) {
            return;
        }

        const lineHeight = this.getAiSummaryLineHeightPx(aiResult);
        const collapsedHeight = lineHeight * this.AI_SUMMARY_COLLAPSED_LINES;
        const hasOverflow = aiResult.scrollHeight > collapsedHeight + 1;
        const labels = EikaSearch.getAiSummaryToggleLabels(aiToggleButton);

        if (!hasOverflow) {
            this.aiSummaryExpanded = false;
            aiSummary.classList.remove(
                this.AI_RESULT_CLASS_HAS_OVERFLOW,
                this.AI_RESULT_CLASS_COLLAPSED,
                this.AI_RESULT_CLASS_EXPANDED,
            );
            aiResult.classList.remove(
                this.AI_RESULT_CLASS_HAS_OVERFLOW,
                this.AI_RESULT_CLASS_COLLAPSED,
                this.AI_RESULT_CLASS_EXPANDED,
            );
            EikaSearch.setAiSummaryCollapsedDimensions(aiResult, collapsedHeight);
            aiToggle.style.display = 'none';
            if (aiInfo) {
                aiInfo.style.display = 'none';
            }
            if (aiToggleButtonLabel) {
                aiToggleButtonLabel.textContent = labels.more;
                aiToggleButton?.setAttribute('data-track-linkname', labels.more);
            }
            aiToggleButton.setAttribute('aria-expanded', 'false');
            return;
        }

        aiToggle.style.removeProperty('display');

        if (this.aiSummaryExpanded) {
            aiSummary.classList.add(this.AI_RESULT_CLASS_HAS_OVERFLOW, this.AI_RESULT_CLASS_EXPANDED);
            aiSummary.classList.remove(this.AI_RESULT_CLASS_COLLAPSED);
            aiResult.classList.add(this.AI_RESULT_CLASS_HAS_OVERFLOW, this.AI_RESULT_CLASS_EXPANDED);
            aiResult.classList.remove(this.AI_RESULT_CLASS_COLLAPSED);
            aiResult.style.removeProperty('height');
            aiResult.style.removeProperty('max-height');
            aiResult.style.removeProperty('overflow');
            if (aiInfo) {
                aiInfo.style.removeProperty('display');
            }
            if (aiToggleButtonLabel) {
                aiToggleButtonLabel.textContent = labels.less;
                aiToggleButton?.setAttribute('data-track-linkname', labels.less);
            }
            aiToggleButton.setAttribute('aria-expanded', 'true');
            return;
        }

        aiSummary.classList.add(this.AI_RESULT_CLASS_HAS_OVERFLOW, this.AI_RESULT_CLASS_COLLAPSED);
        aiSummary.classList.remove(this.AI_RESULT_CLASS_EXPANDED);
        aiResult.classList.add(this.AI_RESULT_CLASS_HAS_OVERFLOW, this.AI_RESULT_CLASS_COLLAPSED);
        aiResult.classList.remove(this.AI_RESULT_CLASS_EXPANDED);
        EikaSearch.setAiSummaryCollapsedDimensions(aiResult, collapsedHeight);
        if (aiInfo) {
            aiInfo.style.display = 'none';
        }
        if (aiToggleButtonLabel) {
            aiToggleButtonLabel.textContent = labels.more;
            aiToggleButton?.setAttribute('data-track-linkname', labels.more);
        }
        aiToggleButton.setAttribute('aria-expanded', 'false');
    }

    resetAiSummaryExpansionState(): void {
        this.aiSummaryExpanded = false;

        const aiContainer = this.searchForm.querySelector(this.SELECTOR_AI_CONTAINER) as HTMLElement | null;
        const aiSummary = aiContainer?.querySelector<HTMLElement>(this.SELECTOR_AI_SUMMARY);
        const aiResult = aiContainer?.querySelector(this.SELECTOR_AI_RESULT) as HTMLElement | null;
        const aiInfo = aiContainer?.querySelector<HTMLElement>(this.SELECTOR_AI_INFO);
        const aiToggle = aiContainer?.querySelector<HTMLElement>(this.SELECTOR_AI_TOGGLE);
        const aiToggleButton = aiToggle?.querySelector<HTMLButtonElement>(this.SELECTOR_AI_TOGGLE_BUTTON);
        const aiToggleButtonLabel = aiToggleButton?.querySelector<HTMLSpanElement>('.sgw-button-label');

        if (aiSummary) {
            aiSummary.classList.remove(
                this.AI_RESULT_CLASS_HAS_OVERFLOW,
                this.AI_RESULT_CLASS_COLLAPSED,
                this.AI_RESULT_CLASS_EXPANDED,
            );
        }

        if (aiResult) {
            const lineHeight = this.getAiSummaryLineHeightPx(aiResult);
            EikaSearch.setAiSummaryCollapsedDimensions(aiResult, lineHeight * this.AI_SUMMARY_COLLAPSED_LINES);
            aiResult.classList.remove(
                this.AI_RESULT_CLASS_HAS_OVERFLOW,
                this.AI_RESULT_CLASS_COLLAPSED,
                this.AI_RESULT_CLASS_EXPANDED,
            );
        }

        if (aiInfo) {
            aiInfo.style.display = 'none';
        }

        if (aiToggle) {
            aiToggle.style.display = 'none';
        }

        if (aiToggleButton) {
            const labels = EikaSearch.getAiSummaryToggleLabels(aiToggleButton);
            if (aiToggleButtonLabel) {
                aiToggleButtonLabel.textContent = labels.more;
            }
            aiToggleButton.setAttribute('aria-expanded', 'false');
        }
    }

    clearResults(): void {
        this.setFilterRadios();
        this.searchResults!.innerHTML = '';
        this.searchResultsContainer!.style.display = 'block';
        this.currentPage = 0;
        if (this.searchResultsText) {
            this.setNumResults();
            this.searchResultsText.style.visibility = 'hidden';
        }
        if (this.aiEnabled) {
            const aiContainer = this.searchForm.querySelector(this.SELECTOR_AI_CONTAINER) as HTMLElement | null;
            const aiResult = aiContainer?.querySelector(this.SELECTOR_AI_RESULT) as HTMLElement | null;
            this.removeAiSummarySkeleton();
            if (aiResult) {
                aiResult.innerHTML = '';
            }
            if (aiContainer) {
                aiContainer.style.display = 'none';
            }
            this.setAiActionButtonsVisible(false);
            this.ToggleFeedbackMessage(false);
            this.aiSummaryBuffer = '';
            this.resetAiSummaryExpansionState();
        }
    }

    fauxResults(privateNum: number = 0, corporateNum: number = 0): void {
        this.clearResults();
        this.setFilterRadios(privateNum, corporateNum);
        this.noResults();
    }

    noResults(): void {
        if (this.searchResults) {
            if (this.searchResultsText) {
                const numResText = this.searchResultsText.querySelector('span');
                numResText!.textContent = '0';
            }
            const noResultsLabel = this.searchResults.getAttribute('data-label-noresults') ?? 'No results!';
            this.searchResults.innerHTML = `<div class="sgw-attention-message sgw-attention-message__info type-info">
                <div class="sgw-attention-message__inner">
                    <span class="sgw-icon sgw-attention-message__icon">
                        <span class="icon icon--info-24 icon--24" aria-hidden="true"></span>
                    </span>
                    <div class="message-text__container">
                        <p class="message-text">${noResultsLabel}</p>
                    </div>
                </div>
            </div>`;
        }
    }

    setFilterRadios(numPrivateResults: number = 0, numCorporateResults: number = 0) {
        if (!this.searchResultsFilterContainer) return;

        const corpRadio = this.searchResultsFilterContainer.querySelector(
            this.SELECTOR_CORPORATE_RADIO,
        ) as HTMLInputElement;
        const corpLabel = this.searchResultsFilterContainer.querySelector(
            this.SELECTOR_CORPORATE_OPTIONS_LABEL,
        ) as HTMLElement;
        const privRadio = this.searchResultsFilterContainer.querySelector(
            this.SELECTOR_PRIVATE_RADIO,
        ) as HTMLInputElement;
        const privLabel = this.searchResultsFilterContainer.querySelector(
            this.SELECTOR_PRIVATE_OPTIONS_LABEL,
        ) as HTMLElement;

        if (corpLabel && privLabel) {
            privLabel!.textContent = numPrivateResults.toString();
            corpLabel!.textContent = numCorporateResults.toString();
        }

        if (corpRadio && privRadio) {
            const hasPrivateResults = numPrivateResults > 0;
            const hasCorporateResults = numCorporateResults > 0;

            privRadio.disabled = !hasPrivateResults;
            corpRadio.disabled = !hasCorporateResults;

            if (numPrivateResults === 0 && numCorporateResults === 0) {
                this.searchResultsFilterContainer.style.display = 'none';
            } else {
                this.searchResultsFilterContainer.style.display = '';
            }
        }
        if (this.isCorporateHidden()) {
            this.searchResultsFilterContainer.style.display = 'none';
        }
    }

    isCorporateHidden(): boolean {
        const hideBedrift = this.searchForm.querySelector<HTMLInputElement>(this.SELECTOR_HIDE_BEDRIFT);
        return !!(hideBedrift && hideBedrift?.value === 'true');
    }

    setNumResults(currentPage: number = 0, total: number = 0): void {
        if (this.searchResultsText) {
            const showingText = this.searchResultsText.querySelector(this.SELECTOR_RESULTS_SHOWING) as HTMLElement;
            const numResText = this.searchResultsText.querySelector(this.SELECTOR_RESULTS_TOTAL) as HTMLElement;
            if (showingText && numResText) {
                numResText.textContent = total.toString();
                if (total <= 0) {
                    showingText.textContent = '0';
                    return;
                }
                let showing;
                if (currentPage === 0) {
                    const max = total < this.numFirstResults ? total.toString() : this.numFirstResults.toString();
                    showing = `1-${max}`;
                } else {
                    let end = currentPage * 10 + this.numFirstResults;
                    end = total < end ? total : end;
                    showing = `1-${end}`;
                }
                showingText.textContent = showing;
            }
        }
    }

    populateResults(results: SearchResults, currentPage: number, query: string, scope: SearchScope): void {
        if (this.searchResults) {
            this.searchResultsText?.style.removeProperty('visibility');
            this.removeInlineErrorMessage();

            const numPrivate = results.Aggregations.false ?? 0;
            const numCorporate = results.Aggregations.true ?? 0;
            const corpSearchResults = Number(numCorporate) || 0;
            const privSearchResults = Number(numPrivate) || 0;

            let totalSearchResults = privSearchResults;
            if (scope === SEARCH_SCOPE.CORPORATE) {
                totalSearchResults = corpSearchResults;
            } else if (scope === SEARCH_SCOPE.ALL) {
                totalSearchResults = corpSearchResults + privSearchResults;
            }

            if (totalSearchResults === 0 && this.aiEnabled && currentPage === 0) {
                this.hideAiSummaryContainerAndSkeleton();
            }

            this.setFilterRadios(privSearchResults, corpSearchResults);

            if (this.searchResultsFilterContainer && !this.isCorporateHidden()) {
                this.searchResultsFilterContainer.style.display = 'block';
            }

            if (currentPage === 0) {
                EikaSearch.GTM(query, scope, privSearchResults, corpSearchResults);
                this.searchResults.style.opacity = '0';
            }

            this.setNumResults(currentPage, totalSearchResults);

            const showMoreBtn = this.searchForm.querySelector(this.SELECTOR_SHOW_MORE);
            if (showMoreBtn) {
                showMoreBtn.remove();
            }

            if (totalSearchResults === 0) {
                this.searchResults.innerHTML = '';
                this.searchResults.style.opacity = '1';
                this.noResults();
                return;
            }

            results.Documents.forEach((item: SearchResultItem) => {
                const titleLabel = item.Elastictitle ? item.Elastictitle : item.Title;
                const description = item.Meta_Description
                    ? item.Meta_Description
                    : `${item.Body_Content?.substring(0, 100) ?? ''}...`;

                const article = html<HTMLElement>`
                    <article class="eika-elastic-search__results-result">
                        <p class="typography-paragraph eika-elastic-search__results-title">
                            <strong>
                                <a
                                    href="${item.Url}"
                                    class="eika-elastic-search__results-link"
                                    data-track-id="sitecore-elasticSearch-results"
                                    data-track-linkname="${piwikFormatName(titleLabel)}"
                                >
                                    ${titleLabel}
                                </a>
                            </strong>
                        </p>
                        <p class="typography-paragraph eika-elastic-search__results-description">${description}</p>
                    </article>
                `;

                this.searchResults!.appendChild(article);
            });

            if (currentPage === 0) {
                setTimeout(() => {
                    if (this.searchResults) this.searchResults.style.opacity = '1';
                }, 60);
            }

            const totalCount = results.TotalCount;
            const loadedCount = this.searchResults!.childElementCount;

            if (loadedCount < totalCount) {
                const moreLabel = this.searchResults.getAttribute('data-label-more') ?? 'Vis flere';
                const showMoreButton = sgwLinkButtonIcon(moreLabel, 'pil-dropdown-ned', 'right');
                const showMoreButtonContainer = html<HTMLDivElement>`
                    <div class="eika-elastic-search__show-more">${showMoreButton}</div>
                `;

                on(showMoreButton, 'click', () => {
                    if (this.isPagingRequestInFlight) {
                        return;
                    }
                    this.currentPage += 1;
                    this.setShowMoreDisabled(true);
                    this.performStreamingSearch(query, scope, this.currentPage);
                });

                this.searchResults!.appendChild(showMoreButtonContainer);
            }
        }
    }

    getGenericErrorText(): string {
        return this.searchResults?.getAttribute('data-label-error') ?? 'Beklager, noe gikk galt. Prøv igjen.';
    }

    renderGenericErrorState(): void {
        if (!this.searchResults) return;
        if (this.searchResultsText) {
            this.searchResultsText.style.visibility = 'hidden';
        }
        const errorMessage = html<HTMLDivElement>`
            <div class="sgw-attention-message sgw-attention-message__info type-info">
                <div class="sgw-attention-message__inner">
                    <span class="sgw-icon sgw-attention-message__icon">
                        <span class="icon icon--info-24 icon--24" aria-hidden="true"></span>
                    </span>
                    <div class="message-text__container">
                        <p class="message-text">${this.getGenericErrorText()}</p>
                    </div>
                </div>
            </div>
        `;
        this.searchResults.innerHTML = '';
        this.searchResults.appendChild(errorMessage);
    }

    removeInlineErrorMessage(): void {
        const el = this.searchResults?.querySelector(this.SELECTOR_PAGINATION_ERROR);
        if (el) {
            el.remove();
        }
    }

    renderInlinePaginationError(): void {
        if (!this.searchResults) return;
        this.removeInlineErrorMessage();
        const inlineError = html<HTMLDivElement>`
            <div
                class="eika-elastic-search__pagination-error sgw-attention-message sgw-attention-message__info type-info"
            >
                <div class="sgw-attention-message__inner">
                    <span class="sgw-icon sgw-attention-message__icon">
                        <span class="icon icon--info-24 icon--24" aria-hidden="true"></span>
                    </span>
                    <div class="message-text__container">
                        <p class="message-text">${this.getGenericErrorText()}</p>
                    </div>
                </div>
            </div>
        `;
        this.searchResults.appendChild(inlineError);
    }

    displayError(error: unknown, page: number) {
        const curr = this.currentPage;

        console.error(`Search failed (${curr}, page=${page})`, error);
    }
}

export default function EikaElasticSearch(elm: string) {
    if (elm) {
        on(document, 'DOMContentLoaded', () => {
            const element = document.querySelector(elm) as HTMLElement;
            if (element) {
                const search = new EikaSearch(element);
                search.init();
            }
        });
    }
}
